import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import { CioConnectionStatus, CioTenantConfig, FakeBetsConfig, FontConfig, GameCardConfig, LiveChatConfigPatch, LiveChatSettings } from "types/api"

import useApi, { parseError } from "util/api"

import {
  AdminSettings,
  AuthSettings,
  BetsTablesConfig,
  CryptoCoinsSettings,
  DepositsAndWithdrawalsLimits,
  GgrSettings,
  GiftSettings,
  LocaleUrlsConfig,
  SettingsHistoryEntry,
  SettingsResetAllOutcome,
  SettingsResetTargetsResponse,
  VerificationFieldsSettings,
  VipSettings,
  WithdrawalSettings
} from "./type"

class SettingsService {
  /** @param tenant reads THAT tenant's settings (`rcoin` differs per tenant); omitted reads the
   *  panel's own, which is what the Settings page manages. */
  public async getAll(tenant?: string): Promise<AdminSettings | void> {
    try {
      const { data } = await useApi.get<IResponse<AdminSettings>>(REQ.ADMIN_DASHBOARD.SETTINGS.ALL, {
        ...(tenant && { params: { tenant } })
      })
      if (data.success) return data.response
    } catch (error) {
      toast.error("Failed to load settings")
    }
  }

  public async updateRCoin(payload: Record<string, boolean>): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.RCOIN, payload)
      return data.success
    } catch {
      toast.error("Failed to update currencies")
      return false
    }
  }

  /** Surfaced verbatim: the backend explains which language it refused and why. */
  public async updateLanguages(payload: Record<string, boolean>): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.LANGUAGES, payload)
      return data.success
    } catch (err) {
      parseError(err, "Failed to update languages")
      return false
    }
  }

  /** Errors are surfaced verbatim: the backend explains exactly why a shape was refused. */
  public async updateLocaleUrls(payload: LocaleUrlsConfig): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.LOCALE_URLS, payload)
      return data.success
    } catch (err) {
      parseError(err, "Failed to update the URL structure")
      return false
    }
  }

  public async updateBlockedGeos(geos: string[]): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.BLOCKED_GEOS, { geos })
      return data.success
    } catch {
      toast.error("Failed to update blocked GEOs")
      return false
    }
  }

  public async updateEmailSignIn(allowed: boolean, field: keyof AuthSettings = "allowUnverifiedSignIn"): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.EMAIL_SIGNIN, { allowed, field })
      return data.success
    } catch {
      toast.error("Failed to update email sign-in setting")
      return false
    }
  }

  public async updateKyc(payload: { required: boolean; threshold: number }): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.KYC, payload)
      return data.success
    } catch {
      toast.error("Failed to update KYC settings")
      return false
    }
  }

  public async getDepositsAndWithdrawalsLimits(): Promise<DepositsAndWithdrawalsLimits | null> {
    try {
      const { data } = await useApi.get<IResponse<DepositsAndWithdrawalsLimits>>(
        REQ.ADMIN_DASHBOARD.SETTINGS.WITHDRAWALS_AND_DEPOSITS_LIMITS
      )
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to load withdrawals and deposits limits")
      return null
    }
  }

  public async updateWithdrawalsAndDepositsLimits(payload: DepositsAndWithdrawalsLimits): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.WITHDRAWALS_AND_DEPOSITS_LIMITS, payload)
      return data.success
    } catch {
      toast.error("Failed to update withdrawals and deposits limits")
      return false
    }
  }

  public async getWithdrawals(): Promise<WithdrawalSettings | null> {
    try {
      const { data } = await useApi.get<IResponse<WithdrawalSettings>>(REQ.ADMIN_DASHBOARD.SETTINGS.WITHDRAWALS)
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to load withdrawal settings")
      return null
    }
  }

  public async updateWithdrawals(payload: WithdrawalSettings): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.WITHDRAWALS, payload)
      return data.success
    } catch {
      toast.error("Failed to update withdrawal settings")
      return false
    }
  }

  /**
   * Outcomes are reported by the caller, which has the translated copy — the service only
   * says whether the call landed.
   */
  public async getResetTargets(): Promise<SettingsResetTargetsResponse | null> {
    try {
      const { data } = await useApi.get<IResponse<SettingsResetTargetsResponse>>(REQ.ADMIN_DASHBOARD.SETTINGS.RESET_TARGETS)
      return data.success ? data.response : null
    } catch {
      return null
    }
  }

  public async resetSetting(target: string): Promise<boolean> {
    try {
      const { data } = await useApi.post(REQ.ADMIN_DASHBOARD.SETTINGS.RESET_SPECIFIC.replace(":target", target))
      return !!data.success
    } catch {
      return false
    }
  }

  /** `confirmation` must be the phrase `getResetTargets` returned; the API rejects anything else. */
  public async resetAllSettings(confirmation: string): Promise<SettingsResetAllOutcome | null> {
    try {
      const { data } = await useApi.post<IResponse<SettingsResetAllOutcome>>(REQ.ADMIN_DASHBOARD.SETTINGS.RESET_ALL, {
        confirm: confirmation
      })
      return data.success ? data.response : null
    } catch {
      return null
    }
  }

  public async getSettingHistory(target: string, limit = 5): Promise<SettingsHistoryEntry[]> {
    try {
      const { data } = await useApi.get<IResponse<SettingsHistoryEntry[]>>(
        REQ.ADMIN_DASHBOARD.SETTINGS.HISTORY.replace(":target", target),
        { params: { limit } }
      )
      return data.success ? (data.response ?? []) : []
    } catch {
      return []
    }
  }

  public async getVip(): Promise<VipSettings | null> {
    try {
      const { data } = await useApi.get<IResponse<VipSettings | null>>(REQ.ADMIN_DASHBOARD.SETTINGS.VIP)
      if (data.success) return data.response ?? null
      return null
    } catch {
      toast.error("Failed to load VIP settings")
      return null
    }
  }

  public async updateVip(payload: VipSettings | null | undefined): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.VIP, payload ?? null)
      return data.success
    } catch {
      toast.error("Failed to update VIP settings")
      return false
    }
  }

  public async getGgr(): Promise<GgrSettings | null> {
    try {
      const { data } = await useApi.get<IResponse<GgrSettings>>(REQ.ADMIN_DASHBOARD.SETTINGS.GGR)
      if (data.success) return data.response
      return null
    } catch (err) {
      parseError(err, "Failed to load GGR settings")
      return null
    }
  }

  public async updateGgr(payload: GgrSettings): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.GGR, payload)
      return data.success
    } catch (err) {
      parseError(err, "Failed to update GGR settings")
      return false
    }
  }

  public async getGgrHistory(params?: { limit?: number; offset?: number; eventType?: string }) {
    try {
      const { data } = await useApi.get(REQ.ADMIN_DASHBOARD.SETTINGS.GGR_HISTORY, { params })
      if (data.success) return data.response
      return null
    } catch (err) {
      parseError(err, "Failed to load GGR history")
      return null
    }
  }

  public async getSportsbookGgr(): Promise<GgrSettings | null> {
    try {
      const { data } = await useApi.get<IResponse<GgrSettings>>(REQ.ADMIN_DASHBOARD.SETTINGS.SPORTSBOOK_GGR)
      if (data.success) return data.response
      return null
    } catch (err) {
      parseError(err, "Failed to load sportsbook GGR settings")
      return null
    }
  }

  public async updateSportsbookGgr(payload: GgrSettings): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.SPORTSBOOK_GGR, payload)
      return data.success
    } catch (err) {
      parseError(err, "Failed to update sportsbook GGR settings")
      return false
    }
  }

  public async getSportsbookGgrHistory(params?: { limit?: number; offset?: number; eventType?: string }) {
    try {
      const { data } = await useApi.get(REQ.ADMIN_DASHBOARD.SETTINGS.SPORTSBOOK_GGR_HISTORY, { params })
      if (data.success) return data.response
      return null
    } catch (err) {
      parseError(err, "Failed to load sportsbook GGR history")
      return null
    }
  }

  public async getAffiliateDefaults() {
    const { data } = await useApi.get<IResponse<any>>(REQ.ADMIN_DASHBOARD.SETTINGS.GET_AFFILIATE_DEFAULTS)
    if (data?.success) return data?.response?.payload ?? data?.response
  }

  public async saveAffiliateDefaults(payload: any) {
    const { data } = await useApi.patch<IResponse<any>>(REQ.ADMIN_DASHBOARD.SETTINGS.UPDATE_AFFILIATE_DEFAULTS, payload)
    if (data?.success) return data?.response
  }

  // Live chat outcomes are reported by the calling hook, where they can be translated.
  public async getLiveChatSettings(): Promise<LiveChatSettings | null> {
    try {
      const { data } = await useApi.get<IResponse<LiveChatSettings>>(REQ.ADMIN_DASHBOARD.SETTINGS.LIVE_CHAT)
      return data.success ? data.response : null
    } catch {
      return null
    }
  }

  public async updateLiveChatSettings(payload: LiveChatConfigPatch): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.LIVE_CHAT, payload)
      return !!data.success
    } catch {
      return false
    }
  }

  public async deleteLiveChatSettings(): Promise<boolean> {
    try {
      const { data } = await useApi.delete(REQ.ADMIN_DASHBOARD.SETTINGS.LIVE_CHAT)
      return !!data.success
    } catch {
      return false
    }
  }

  public async getCioSettings(): Promise<CioTenantConfig | null> {
    try {
      const { data } = await useApi.get<IResponse<CioTenantConfig>>(REQ.ADMIN_DASHBOARD.SETTINGS.CUSTOMERIO)
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to load Customer.io settings")
      return null
    }
  }

  public async updateCioSettings(payload: Partial<CioTenantConfig>): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.CUSTOMERIO, payload)
      return data.success
    } catch {
      toast.error("Failed to update Customer.io settings")
      return false
    }
  }

  public async updateEmailProvider(provider: "nodemailer" | "customerio"): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.EMAIL_PROVIDER, { provider })
      return !!(data.success || data.ok)
    } catch (err: any) {
      const reasons: string[] | undefined = err?.data?.reasons ?? err?.data?.response?.reasons
      if (reasons?.length) {
        reasons.forEach((reason) => toast.error(reason))
      } else {
        toast.error("Failed to update email provider")
      }
      return false
    }
  }

  public async getCryptoCoins(): Promise<CryptoCoinsSettings | null> {
    try {
      const { data } = await useApi.get<IResponse<CryptoCoinsSettings>>(REQ.ADMIN_DASHBOARD.SETTINGS.CRYPTO_COINS)
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to load crypto coins settings")
      return null
    }
  }

  public async updateCryptoCoins(payload: CryptoCoinsSettings): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.CRYPTO_COINS, payload)
      return data.success
    } catch {
      toast.error("Failed to update crypto coins settings")
      return false
    }
  }

  public async deleteCioCredentials(): Promise<boolean> {
    try {
      const { data } = await useApi.delete(REQ.ADMIN_DASHBOARD.SETTINGS.CUSTOMERIO_CREDENTIALS)
      if (data.success) toast.success("Customer.io credentials deleted")
      return !!data.success
    } catch {
      toast.error("Failed to delete Customer.io credentials")
      return false
    }
  }

  public async testCioConnection(): Promise<CioConnectionStatus | null> {
    try {
      const { data } = await useApi.get<IResponse<CioConnectionStatus>>(REQ.ADMIN_DASHBOARD.SETTINGS.CUSTOMERIO_TEST)
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to test Customer.io connection")
      return null
    }
  }

  public async getGifts(): Promise<GiftSettings | null> {
    try {
      const { data } = await useApi.get<IResponse<GiftSettings>>(REQ.ADMIN_DASHBOARD.SETTINGS.GIFTS)
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to load gift settings")
      return null
    }
  }

  public async updateGifts(payload: GiftSettings): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.GIFTS, payload)
      return data.success
    } catch {
      toast.error("Failed to update gift settings")
      return false
    }
  }

  public async updateVerificationFields(payload: VerificationFieldsSettings): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.VERIFICATION_FIELDS, payload)
      return data.success
    } catch {
      toast.error("Failed to update verification fields settings")
      return false
    }
  }

  public async updateBetsTables(payload: BetsTablesConfig): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.BETS_TABLES, payload)
      return data.success
    } catch {
      toast.error("Failed to update bets tables settings")
      return false
    }
  }

  public async getGameCardConfig(): Promise<GameCardConfig | null> {
    try {
      const { data } = await useApi.get<IResponse<GameCardConfig>>(REQ.ADMIN_DASHBOARD.SETTINGS.GAME_CARD_CONFIG)
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to load game card config")
      return null
    }
  }

  public async updateGameCardConfig(payload: Partial<GameCardConfig>): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.GAME_CARD_CONFIG, payload)
      return data.success
    } catch {
      toast.error("Failed to update game card config")
      return false
    }
  }

  public async updateFontConfig(payload: FontConfig): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.FONT_CONFIG, payload)
      return data.success
    } catch {
      toast.error("Failed to update font config")
      return false
    }
  }

  public async getFakeBetsConfig(): Promise<FakeBetsConfig | null> {
    try {
      const { data } = await useApi.get<IResponse<FakeBetsConfig>>(REQ.ADMIN_DASHBOARD.SETTINGS.FAKE_BETS)
      if (data.success) return data.response
      return null
    } catch {
      toast.error("Failed to load fake bets config")
      return null
    }
  }

  public async updateFakeBetsConfig(payload: FakeBetsConfig): Promise<boolean> {
    try {
      const { data } = await useApi.patch(REQ.ADMIN_DASHBOARD.SETTINGS.FAKE_BETS, payload)
      return data.success
    } catch {
      toast.error("Failed to update fake bets config")
      return false
    }
  }

  public async seedFakeBets(count: number): Promise<boolean> {
    try {
      const { data } = await useApi.post(REQ.ADMIN_DASHBOARD.SETTINGS.FAKE_BETS_SEED, { count })
      return data.success
    } catch {
      toast.error("Failed to start fake bets seed run")
      return false
    }
  }
}

export const settingsService = new SettingsService()
